import * as React from 'react';
import { useGetDefaultTimezone } from '#hooks/use-get-default-timezone.hook.ts';
import { XYChart, Tooltip, AxisBottom, AxisLeft, Grid } from '../xychart';
import { useGetLeftAxisLabelOffset } from '../bar-graph/utils/use-calculate-left-axis-label-offset';
import { LinePath } from '../xychart/components/line-path';
import { Circles } from '../xychart/components/circles';
import { useGetRoundedYMax } from '../bar-graph/utils/get-rounded-y-max';
import { LineGraph as LineGraphType } from './types/line-graph';
import { useMonthlyTicks } from '../utils/use-monthly-ticks';

export type MonthlyLineGraphProps<Datum> = LineGraphType<Datum>;

const NUM_Y_TICKS = 5;

export function MonthlyLineGraph<Datum>({
  data,
  accessors,
  title,
  description,
  axisLeftLabel,
  ariaDatumLabel,
  axisBottomLabel,
  renderTooltip,
  yMaxFromEnums,
}: MonthlyLineGraphProps<Datum>) {
  const defaultTimezone = useGetDefaultTimezone();
  const yMin = 0;
  const yMax = useGetRoundedYMax(data, accessors, 100, yMaxFromEnums);
  const labelOffset = useGetLeftAxisLabelOffset(yMax);
  const [leftMargin, setLeftMargin] = React.useState(0);
  const [bottomMargin, setBottomMargin] = React.useState(0);

  const [formatBottomTick, xAxisTicks] = useMonthlyTicks(
    data.map((datum) => accessors.xAccessor(datum)),
    defaultTimezone,
  );

  return (
    <XYChart
      title={title}
      desc={description}
      data={data}
      accessors={accessors}
      margin={{
        left: leftMargin,
        bottom: bottomMargin,
      }}
      xScale={{
        type: 'point',
        padding: 1,
      }}
      yScale={{
        type: 'linear',
        domain: [yMin, yMax],
      }}
    >
      <Grid numTicksRows={NUM_Y_TICKS} columnTickValues={xAxisTicks} />
      <AxisLeft
        label={axisLeftLabel}
        labelOffset={labelOffset}
        numTicks={NUM_Y_TICKS}
        hideTicks
        onWidthChange={(width) => setLeftMargin(width)}
      />
      <AxisBottom
        label={axisBottomLabel}
        tickValues={xAxisTicks}
        tickFormat={formatBottomTick}
        onHeightChange={(height) => setBottomMargin(height)}
      />
      <LinePath accessors={accessors} data={data} />
      <Circles
        ariaDatumLabel={ariaDatumLabel}
        accessors={accessors}
        data={data}
      />
      <Tooltip<Datum> snapToDatum>{renderTooltip}</Tooltip>
    </XYChart>
  );
}
